import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {LogoIcon} from "../components/Icon/Logo.icon.tsx";
import {FC, useCallback} from "react";
import {UserAccountInfo} from "../model/UserAccountInfo.ts";
import {AlertComponent} from "../components/AlertComponent.tsx";
import {useRecoilState} from "recoil";
import {alertsAtom} from "../state/alerts.ts";
import {isAdmin} from "../utils.ts";
import {Button} from "../components/Button.tsx";
import useSessionTimeout from "../hooks/useSessionTimeout.ts";
import {SimpleDialog} from "../components/SimpleDialog.tsx";

interface MainMenuProps {
    isUserTabActive: boolean
    isGroupTabActive: boolean
    isApplicationTabActive: boolean
    isDataLimitationTabActive: boolean
    onTabUsersClick: () => void
    onTabGroupsClick: () => void
    onTabApplicationsClick: () => void
    onTabDataLimitationsClick: () => void
    accountInfo: UserAccountInfo
}
const MainMenu: FC<MainMenuProps> = ({ isUserTabActive, isApplicationTabActive, isDataLimitationTabActive, isGroupTabActive, onTabApplicationsClick, onTabDataLimitationsClick, onTabUsersClick, onTabGroupsClick, accountInfo }) => {
    return <div className="flex justify-start items-center">
        <div className={isUserTabActive? `border-b-2 border-rose-600 py-1 px-10 font-bold text-sm text-black h-[50px] items-center flex cursor-pointer` : `border-b-2 py-1 px-10 font-bold text-sm text-black h-[50px] flex items-center cursor-pointer`} onClick={onTabUsersClick}>
            <b className={isUserTabActive ? "font-bold text-rose-600" : "font-bold"}>Users</b>
        </div>
        <div className={isGroupTabActive  ? `border-b-2 border-rose-600 py-1 px-10 font-bold text-sm text-black h-[50px] items-center flex cursor-pointer` : `border-b-2 py-1 px-10 font-bold text-sm text-black h-[50px] flex items-center cursor-pointer`} onClick={onTabGroupsClick}>
            <b className={isGroupTabActive ? "font-bold text-rose-600" : "font-bold"}>Groups</b>
        </div>
        {!isAdmin(accountInfo) ? null : <div className={isApplicationTabActive ? `border-b-2 border-rose-600 py-1 px-10 font-bold text-sm text-black h-[50px] items-center flex cursor-pointer` : `border-b-2 py-1 px-10 font-bold text-sm text-black h-[50px] flex items-center cursor-pointer`} onClick={onTabApplicationsClick}>
            <b className={isApplicationTabActive ? "font-bold text-rose-600" : "font-bold"}>Applications</b>
        </div>}
        {!isAdmin(accountInfo) ? null :<div className={isDataLimitationTabActive ? `border-b-2 border-rose-600 py-1 px-10 font-bold text-sm text-black h-[50px] items-center flex cursor-pointer` : `border-b-2 py-1 px-10 font-bold text-sm text-black h-[50px] flex items-center cursor-pointer`} onClick={onTabDataLimitationsClick}>
            <b className={isDataLimitationTabActive ? "font-bold text-rose-600" : "font-bold"}>Data Limitations</b>
        </div>}
        <div className="grow border-b-2 py-1 px-4  h-[50px]">
        </div>
    </div>
}
interface TopHeaderProps {
    logout: () => void;
    accountInfo: UserAccountInfo
}

const TopHeader: FC<TopHeaderProps> = ({accountInfo, logout}) => {
    return <div className="flex items-center justify-between h-16 bg-linear px-5">
        <span className="bg-primary"><LogoIcon /></span>
        <div className="flex items-center justify-center">
            {accountInfo.account != null && <div className="p-2 m-2 font-bold text-gray-800 whitespace-nowrap">Logged in as {accountInfo.account?.name ?? `${accountInfo.user?.first_name ?? ""} ${accountInfo.user?.last_name ?? ""}`} ({accountInfo.roles?.join(", ")})</div>}
            <Button onClick={logout} primary={false} name="Log out" disabled={false} />
        </div>
    </div>
}
const AlertManagement: FC = () => {
    const [alerts, setAlerts] = useRecoilState(alertsAtom)

    const onCancel = (created: number) => {
        setAlerts(currState => {
            const alert = currState.find(alert => alert.created === created)
            if(!alert) return currState
            return [...currState.filter(alert => alert.created !== created)]
        })
    }
    return  <div className="absolute w-full top-20 right-5 w-3/4 sm:top-20 sm:right-5 sm:w-1/3">
        {[...alerts].sort(alert => alert.created).slice(0,3).map(alert => <AlertComponent name={alert.name} description={alert.description} onCancel={onCancel} created={alert.created} type={alert.type} key={alert.created} />)}
    </div>
}

export const LazyMainPage: FC<MainPageProps> = ({accountInfo, logout}) => {
    const isUserTabActive = window.location.hash == "" || window.location.hash == "#/" || location.hash.startsWith("#/users")
    const isGroupTabActive = window.location.hash.startsWith("#/groups")
    const isApplicationTabActive = window.location.hash.startsWith("#/applications")
    const isDataLimitationTabActive = window.location.hash.startsWith("#/data-limitations")
    const onTabUsersClick = () => window.location.href = "/#/"
    const onTabGroupsClick = () => window.location.href = "/#/groups"
    const onTabApplicationsClick = () => window.location.href = "/#/applications"
    const onTabDataLimitationsClick = () => window.location.href = "/#/data-limitations"

    return<div className="App max-w-[1680px]">
        <TopHeader logout={logout} accountInfo={accountInfo} />
        <div className="p-2 md:p-6 bg-white">
            <MainMenu isUserTabActive={isUserTabActive} isGroupTabActive={isGroupTabActive} isApplicationTabActive={isApplicationTabActive} isDataLimitationTabActive={isDataLimitationTabActive} onTabUsersClick={onTabUsersClick} onTabGroupsClick={onTabGroupsClick} onTabApplicationsClick={onTabApplicationsClick} onTabDataLimitationsClick={onTabDataLimitationsClick} accountInfo={accountInfo} />
            <Outlet />
            <AlertManagement />
        </div>
    </div>
}

interface MainPageProps {
    logout: () => void;
    accountInfo: UserAccountInfo
}
const MainPage: FC<MainPageProps> = ( {logout, accountInfo}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { isOpenLogOut, onResetSession } = useSessionTimeout(logout);

    const onTabUsersClick = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const onTabGroupsClick = useCallback(() => {
        navigate("/groups");
    }, [navigate]);

    const onTabApplicationsClick = useCallback(() => {
        navigate("applications");
    }, [navigate]);

    const onTabDataLimitationsClick = useCallback(() => {
        navigate("/data-limitations");
    }, [navigate]);

    const isUserTabActive = location.pathname == "/" || location.pathname.startsWith("/users")
    const isGroupTabActive = location.pathname.startsWith("/groups")
    const isApplicationTabActive = location.pathname.startsWith("/applications")
    const isDataLimitationTabActive = location.pathname.startsWith("/data-limitations")

    return <div className="App max-w-[1680px]">
        <TopHeader logout={logout} accountInfo={accountInfo} />
        <div className="p-2 md:p-6 bg-white">
            <MainMenu isUserTabActive={isUserTabActive} isGroupTabActive={isGroupTabActive} isApplicationTabActive={isApplicationTabActive} isDataLimitationTabActive={isDataLimitationTabActive} onTabUsersClick={onTabUsersClick} onTabGroupsClick={onTabGroupsClick} onTabApplicationsClick={onTabApplicationsClick} onTabDataLimitationsClick={onTabDataLimitationsClick} accountInfo={accountInfo} />
            <Outlet />
            <AlertManagement />
        </div>
        {isOpenLogOut && <SimpleDialog onClose={logout} cancelText={'Sign Out'} confirmText={'Continue'} title={'Session has expired'} text={'You have been inactive for 15 minutes. Do you want to stay logged in?'} onSubmit={onResetSession} />}
    </div>
}

export default MainPage;